import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton,
  TextField, MenuItem, Button, ListSubheader,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import SavingOverlay from '../SavingOverlay';
import {
  patientTherapyPlanService, therapyPlanErrorMessage,
  TherapyGoalRow, TherapyFilterOption, TherapyStageRow,
} from '../../services/patientTherapyPlanService';
import { colors, typography } from '../../theme/designSystem';

/**
 * Formulário do objetivo do plano terapêutico (RN-541i): cria e edita.
 *
 * As duas escalas são o coração do registro — o ESTADO ATUAL diz de onde o paciente parte e o
 * ALVO aonde deve chegar, ambos de 0 a 10. O atingimento não se digita aqui: vem das notas
 * lançadas nas evoluções das sessões.
 *
 * Objetivo concluído não abre para edição: o parecer já foi assinado.
 */
interface Props {
  isOpen: boolean;
  goal: TherapyGoalRow | null;
  patientId: string;
  periods: TherapyFilterOption[];
  teams: TherapyFilterOption[];
  professionals: TherapyFilterOption[];
  professionalTypes: TherapyFilterOption[];
  diagnoses: TherapyFilterOption[];
  priorities: string[];
  stages: TherapyStageRow[];
  onClose: () => void;
  onSaved: () => void;
}

const MAX_TITULO = 255;
const MAX_TEXTO = 1000;
const MAX_OBS = 2000;
const ESCALA = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const TherapyGoalFormModal: React.FC<Props> = ({
  isOpen, goal, patientId, periods, teams, professionals, professionalTypes,
  diagnoses, priorities, stages, onClose, onSaved,
}) => {
  const [title, setTitle] = useState('');
  const [goalText, setGoalText] = useState('');
  const [justify, setJustify] = useState('');
  const [metric, setMetric] = useState('');
  const [notes, setNotes] = useState('');
  const [currentScale, setCurrentScale] = useState<number | ''>('');
  const [targetScale, setTargetScale] = useState<number | ''>('');
  const [sessionsQuantity, setSessionsQuantity] = useState<number | ''>('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [responsibleId, setResponsibleId] = useState('');
  const [professionalTypeId, setProfessionalTypeId] = useState('');
  const [diagnosisId, setDiagnosisId] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [finalDate, setFinalDate] = useState('');

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    setTitle(goal?.title ?? '');
    setGoalText(goal?.goal ?? '');
    setJustify(goal?.justify ?? '');
    setMetric(goal?.metric ?? '');
    setNotes(goal?.notes ?? '');
    setCurrentScale(goal?.currentScale ?? '');
    setTargetScale(goal?.targetScale ?? '');
    setSessionsQuantity(goal?.sessionsQuantity ?? '');
    setStatus(goal?.status ?? stages[0]?.name ?? '');
    setPriority(goal?.priority ?? '');
    setPeriodId(goal?.periodId ?? '');
    setTeamId(goal?.teamId ?? '');
    setResponsibleId(goal?.responsibleId ?? '');
    setProfessionalTypeId('');
    setDiagnosisId('');
    setInitialDate('');
    setFinalDate('');
  }, [isOpen, goal, stages]);

  const salvar = async () => {
    setFormError(null);
    if (!title.trim()) {
      setFormError('Informe o título do objetivo.');
      return;
    }
    if (currentScale !== '' && targetScale !== '' && Number(targetScale) < Number(currentScale)) {
      setFormError('A meta não pode ser menor que o estado atual.');
      return;
    }
    if (initialDate && finalDate && finalDate < initialDate) {
      setFormError('A data final não pode ser anterior à inicial.');
      return;
    }

    setSaving(true);
    try {
      await patientTherapyPlanService.saveGoal({
        id: goal?.id,
        patientId,
        title: title.trim(),
        goal: goalText.trim() || null,
        justify: justify.trim() || null,
        metric: metric.trim() || null,
        notes: notes.trim() || null,
        currentScale: currentScale === '' ? null : Number(currentScale),
        targetScale: targetScale === '' ? null : Number(targetScale),
        sessionsQuantity: sessionsQuantity === '' ? null : Number(sessionsQuantity),
        status: status || null,
        priority: priority || null,
        periodId: periodId || null,
        teamId: teamId || null,
        responsibleId: responsibleId || null,
        professionalTypeId: professionalTypeId || null,
        diagnosisId: diagnosisId || null,
        initialDate: initialDate ? `${initialDate}T00:00:00Z` : null,
        finalDate: finalDate ? `${finalDate}T00:00:00Z` : null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setFormError(therapyPlanErrorMessage(e, 'Não foi possível salvar o objetivo.'));
    } finally {
      setSaving(false);
    }
  };

  const campoEscala = (
    label: string,
    value: number | '',
    onChange: (v: number | '') => void,
    ajuda: string,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      helperText={ajuda}
      InputLabelProps={{ shrink: true }}
      sx={{ flex: '1 1 140px' }}
    >
      <MenuItem value="">Não informado</MenuItem>
      {ESCALA.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
    </TextField>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '90vh' } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary, color: colors.white, padding: '1.5rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.25rem', fontWeight: typography.fontWeight.semibold }}>
          {goal ? 'Editar objetivo' : 'Novo objetivo'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.white, padding: '0.25rem' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        <TextField
          fullWidth
          size="small"
          label="Título do objetivo"
          value={title}
          onChange={e => setTitle(e.target.value)}
          inputProps={{ maxLength: MAX_TITULO }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              label="O que se pretende alcançar"
              value={goalText}
              onChange={e => setGoalText(e.target.value)}
              inputProps={{ maxLength: MAX_TEXTO }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Justificativa"
              value={justify}
              onChange={e => setJustify(e.target.value)}
              inputProps={{ maxLength: MAX_TEXTO }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Como medir"
              placeholder="Escala GAS, CIF, observação direta..."
              value={metric}
              onChange={e => setMetric(e.target.value)}
              inputProps={{ maxLength: MAX_TEXTO }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Observações"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              inputProps={{ maxLength: MAX_OBS }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={2}
            />
          </Box>

          <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              {campoEscala('Estado atual', currentScale, setCurrentScale, 'De onde parte (0 a 10)')}
              {campoEscala('Meta', targetScale, setTargetScale, 'Aonde deve chegar (0 a 10)')}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                size="small"
                type="number"
                label="Sessões previstas"
                value={sessionsQuantity}
                onChange={e => setSessionsQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 0, max: 999 }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              />
              <TextField
                select
                size="small"
                label="Prioridade"
                value={priority}
                onChange={e => setPriority(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              >
                <MenuItem value="">Não informada</MenuItem>
                {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                select
                size="small"
                label="Situação"
                value={status}
                onChange={e => setStatus(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              >
                {stages.map(s => <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="Especialidade"
                value={professionalTypeId}
                onChange={e => setProfessionalTypeId(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              >
                <MenuItem value="">Não informada</MenuItem>
                {professionalTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Box>

            <TextField
              select
              fullWidth
              size="small"
              label="Período"
              value={periodId}
              onChange={e => setPeriodId(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Sem período</MenuItem>
              {periods.some(p => p.isShared) && (
                <ListSubheader sx={{ fontSize: '0.72rem', lineHeight: 2 }}>Da clínica</ListSubheader>
              )}
              {periods.filter(p => p.isShared).map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
              {periods.some(p => !p.isShared) && (
                <ListSubheader sx={{ fontSize: '0.72rem', lineHeight: 2 }}>Deste paciente</ListSubheader>
              )}
              {periods.filter(p => !p.isShared).map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                select
                size="small"
                label="Responsável"
                value={responsibleId}
                onChange={e => setResponsibleId(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              >
                <MenuItem value="">Não informado</MenuItem>
                {professionals.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="Equipe"
                value={teamId}
                onChange={e => setTeamId(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              >
                <MenuItem value="">Não informada</MenuItem>
                {teams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Box>

            <TextField
              select
              fullWidth
              size="small"
              label="Diagnóstico (CID)"
              value={diagnosisId}
              onChange={e => setDiagnosisId(e.target.value)}
              helperText={diagnoses.length === 0 ? 'Este paciente não tem diagnóstico registrado' : ' '}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Sem vínculo</MenuItem>
              {diagnoses.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="date"
                label="Data inicial"
                value={initialDate}
                onChange={e => setInitialDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              />
              <TextField
                size="small"
                type="date"
                label="Data final"
                value={finalDate}
                onChange={e => setFinalDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 130px' }}
              />
            </Box>
          </Box>
        </Box>

        {formError && (
          <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mt: 2 }}>
            {formError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem' }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button onClick={salvar} variant="contained" disabled={saving} sx={{ textTransform: 'none' }}>
          Salvar
        </Button>
      </DialogActions>

      <SavingOverlay open={saving} message="Salvando objetivo..." />
    </Dialog>
  );
};

export default TherapyGoalFormModal;
